import React, { createContext, useContext, useEffect, useState } from 'react';
import { useAuth } from './AuthContext';
import { 
  analytics, 
  BusinessMetric, 
  AIInsight, 
  DataSource,
  AnalyticsGoal,
  AnalyticsAlert
} from '../lib/analytics';

interface AnalyticsContextType {
  metrics: BusinessMetric[];
  insights: AIInsight[];
  dataSources: DataSource[];
  goals: AnalyticsGoal[];
  alerts: AnalyticsAlert[];
  loading: boolean;
  error: Error | null;
  refreshMetrics: () => Promise<void>;
  refreshInsights: () => Promise<void>;
  refreshDataSources: () => Promise<void>;
  refreshGoals: () => Promise<void>;
  refreshAlerts: () => Promise<void>;
  markAlertAsRead: (alertId: string) => Promise<void>;
  markAllAlertsAsRead: () => Promise<void>;
  createGoal: (goal: Omit<AnalyticsGoal, 'id' | 'created_at' | 'updated_at' | 'status'>) => Promise<void>;
  updateGoal: (goalId: string, updates: Partial<AnalyticsGoal>) => Promise<void>;
}

const AnalyticsContext = createContext<AnalyticsContextType | undefined>(undefined);

export function AnalyticsProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const [metrics, setMetrics] = useState<BusinessMetric[]>([]);
  const [insights, setInsights] = useState<AIInsight[]>([]);
  const [dataSources, setDataSources] = useState<DataSource[]>([]);
  const [goals, setGoals] = useState<AnalyticsGoal[]>([]);
  const [alerts, setAlerts] = useState<AnalyticsAlert[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const refreshMetrics = async () => {
    if (!user) return;
    try {
      const data = await analytics.getBusinessMetrics(user.id);
      setMetrics(data);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch metrics'));
    }
  };

  const refreshInsights = async () => {
    if (!user) return;
    try {
      const data = await analytics.getAIInsights(user.id);
      setInsights(data);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch insights'));
    }
  };

  const refreshDataSources = async () => {
    if (!user) return;
    try {
      const data = await analytics.getDataSources(user.id);
      setDataSources(data);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch data sources'));
    }
  };

  const refreshGoals = async () => {
    if (!user) return;
    try {
      const data = await analytics.getAnalyticsGoals(user.id);
      setGoals(data);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch goals'));
    }
  };

  const refreshAlerts = async () => {
    if (!user) return;
    try {
      const data = await analytics.getAnalyticsAlerts(user.id);
      setAlerts(data);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch alerts'));
    }
  };

  const markAlertAsRead = async (alertId: string) => {
    if (!user) return;
    try {
      await analytics.markAlertAsRead(alertId);
      setAlerts(prev => prev.map(alert => 
        alert.id === alertId ? { ...alert, is_read: true } : alert
      ));
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to mark alert as read'));
    }
  };

  const markAllAlertsAsRead = async () => {
    if (!user) return;
    try {
      await analytics.markAllAlertsAsRead(user.id);
      setAlerts(prev => prev.map(alert => ({ ...alert, is_read: true })));
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to mark all alerts as read'));
    }
  };

  const createGoal = async (goal: Omit<AnalyticsGoal, 'id' | 'created_at' | 'updated_at' | 'status'>) => {
    if (!user) return;
    try {
      const newGoal = await analytics.createAnalyticsGoal(goal);
      setGoals(prev => [newGoal, ...prev]);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to create goal'));
    }
  };

  const updateGoal = async (goalId: string, updates: Partial<AnalyticsGoal>) => {
    if (!user) return;
    try {
      const updatedGoal = await analytics.updateAnalyticsGoal(goalId, updates);
      setGoals(prev => prev.map(goal => 
        goal.id === goalId ? updatedGoal : goal
      ));
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to update goal'));
    }
  };

  useEffect(() => {
    if (user) {
      setLoading(true);
      Promise.all([
        refreshMetrics(),
        refreshInsights(),
        refreshDataSources(),
        refreshGoals(),
        refreshAlerts()
      ]).finally(() => setLoading(false));
    }
  }, [user]);

  // Poll for new alerts every minute
  useEffect(() => {
    if (!user) return;

    const pollInterval = setInterval(refreshAlerts, 60000);
    return () => clearInterval(pollInterval);
  }, [user]);

  return (
    <AnalyticsContext.Provider value={{
      metrics,
      insights,
      dataSources,
      goals,
      alerts,
      loading,
      error,
      refreshMetrics,
      refreshInsights,
      refreshDataSources,
      refreshGoals,
      refreshAlerts,
      markAlertAsRead,
      markAllAlertsAsRead,
      createGoal,
      updateGoal
    }}>
      {children}
    </AnalyticsContext.Provider>
  );
}

export function useAnalyticsContext() {
  const context = useContext(AnalyticsContext);
  if (context === undefined) {
    throw new Error('useAnalyticsContext must be used within an AnalyticsProvider');
  }
  return context;
}